import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Pickaxe, X } from "lucide-react"
import { api } from "@/app/api/client"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { useNoteMutations } from "@/app/hooks/useNoteMutations"
import { useClientTheme } from "@/app/components/ThemeContext"
import { NotesSection } from "@/app/components/NotesSection"
import { KindIcon, KIND_LABELS } from "@/app/components/KindBadge"
import { LoadingState } from "@/app/components/LoadingState"
import { DataCard } from "@/app/components/DataCard"
import { WellSummaryModal } from "@/app/components/WellSummaryModal"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { txTypeColor } from "@/app/lib/txColors"
import type { ClientTheme } from "@/app/lib/themes"
import type { InventoryRow } from "@/app/components/InventoryTable"
import type { StrDetailResponse, Transaction, Well } from "@/app/types/inventory"

interface Props {
  row: InventoryRow
  onClose: () => void
}

// The middle columns follow the section's kind: leasehold carries NRI +
// ORRI Res., mineral carries Royalty + Legal Desc.
const LEASEHOLD_TX_HEADERS = [
  "TX #",
  "Date",
  "Type",
  "Net Acres",
  "NRI",
  "ORRI Res.",
  "Counterparty",
  "Bk/Pg",
]
const MINERAL_TX_HEADERS = [
  "TX #",
  "Date",
  "Type",
  "Net Acres",
  "Royalty",
  "Legal Desc",
  "Counterparty",
  "Bk/Pg",
]
const WELL_HEADERS = ["Well", "Operator", "Interests", "WI", "RI"]

// Net acres for display: divestitures show as a negative reduction.
function displayNetAcres(type: string, na: number): string {
  const isDiv = type === "DIV" || na < 0
  const magnitude = formatNumber(Math.abs(na), 4)
  return isDiv ? `-${magnitude}` : magnitude
}

export function StrDetailModal({ row, onClose }: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()
  const sectionNumber = row.str.split("-")[0]
  const [activeWell, setActiveWell] = useState<Well | null>(null)
  const { canWrite } = useCurrentUser()

  const { data, isLoading } = useQuery<StrDetailResponse>({
    queryKey: ["inventory", row.str],
    queryFn: () => api.get<StrDetailResponse>(`/inventories/${encodeURIComponent(row.str)}`),
  })

  // Section note: the drill-down payload is the fresh source (the `row` prop
  // is a snapshot from before any edits); invalidating both keys keeps the
  // table's Notes column and this modal in sync after a save/remove.
  const { save: saveNote, remove: removeNote } = useNoteMutations({
    subjectType: "str",
    subjectKey: row.str,
    invalidateKeys: [["inventories"], ["inventory", row.str]],
  })

  const transactions = data?.transactions ?? []
  const wells = data?.wells ?? []

  // Both kinds render stacked, regardless of which tab opened the modal; a
  // section appears only when its kind has transactions for this STR. The ??
  // guards stale cached payloads that predate interest_kind.
  const leaseholdTxs = transactions.filter((t) => (t.interest_kind ?? "leasehold") === "leasehold")
  const mineralTxs = transactions.filter((t) => t.interest_kind === "mineral")

  // Per-STR money figures: capital deployed into acquisitions vs. proceeds
  // recovered from divestitures. Shown only when prices are recorded.
  const totalDeployed = transactions.reduce(
    (s, t) => s + (t.type === "ACQ" ? Number(t.price) || 0 : 0),
    0
  )
  const totalRecovered = transactions.reduce(
    (s, t) => s + (t.type === "DIV" ? Number(t.price) || 0 : 0),
    0
  )
  const hasMoney = totalDeployed > 0 || totalRecovered > 0
  const noteRows = transactions.filter((t) => t.notes && t.notes.trim())
  const money = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  // Per-kind summary stats, read from the row's kind slices. Rows without
  // `kinds` (stale cached payloads) fall back to the top-level leasehold-
  // accurate figures; nil slice values arrive compacted (absent).
  const lh = row.kinds?.leasehold
  const mn = row.kinds?.mineral
  const orriNum = Number(row.orri_value)
  const leaseholdStats = [
    {
      label: "Net Acres",
      value: formatNumber(lh?.net_acres ?? row.net_acres, 4),
      color: theme.brandTeal,
    },
    {
      label: "Weighted NRI",
      value: decimalToPercent(lh?.weighted_nri ?? row.weighted_nri, 4),
      color: theme.textPrimary,
    },
    {
      label: "ORRI Val",
      value: orriNum > 0 ? formatNumber(orriNum, 4) : "—",
      color: orriNum > 0 ? theme.textMuted : theme.textDim,
    },
  ]
  // Royalty only surfaces while the mineral position is actually leased (PT
  // or HBP) — mirrors the Master Inventory Royalty column's gate so the
  // drill-down never contradicts the table behind it.
  const mineralLeased = (mn?.interest_types ?? []).some((t) => t === "PT" || t === "HBP")
  const showMineralRoyalty = mineralLeased && !!mn?.weighted_royalty
  const mineralStats = [
    {
      label: "Net Acres",
      value: formatNumber(mn?.net_acres ?? "0", 4),
      color: theme.brandTeal,
    },
    {
      label: "Royalty",
      value: showMineralRoyalty ? formatNumber(mn!.weighted_royalty!, 4) : "—",
      color: showMineralRoyalty ? theme.textPrimary : theme.textDim,
    },
  ]

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <div
        className={`relative overflow-hidden flex flex-col ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          border: "none",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(960px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? "100dvh" : "88vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}`, flexShrink: 0 }}
        >
          <div>
            <h2
              style={{
                fontFamily: "var(--font-data)",
                fontSize: "17px",
                fontWeight: 700,
                color: theme.textPrimary,
              }}
            >
              Section {sectionNumber} — {row.township_range}
            </h2>
            <p
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.textMuted,
                marginTop: "2px",
              }}
            >
              {row.str}
              {row.county ? ` · ${row.county} County` : ""}
              {row.operator ? ` · ${row.operator}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ml-4"
            style={{
              color: theme.textMuted,
              backgroundColor: theme.bgSurface2,
              border: `1px solid ${theme.borderColor}`,
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Financials — capital deployed vs. recovered (only when prices exist) */}
        {hasMoney && (
          <div
            className="flex items-center gap-8 px-6 py-3"
            style={{
              borderBottom: `1px solid ${theme.borderColor}`,
              backgroundColor: theme.bgSurface2,
              flexShrink: 0,
            }}
          >
            {totalDeployed > 0 && (
              <div className="flex flex-col gap-0.5">
                <span
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "10px",
                    fontWeight: 600,
                    color: theme.textDim,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                  }}
                >
                  Total Deployed
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-data)",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: theme.brandAccent,
                  }}
                >
                  {money(totalDeployed)}
                </span>
              </div>
            )}
            {totalRecovered > 0 && (
              <div className="flex flex-col gap-0.5">
                <span
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "10px",
                    fontWeight: 600,
                    color: theme.textDim,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                  }}
                >
                  Total Recovered
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-data)",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: theme.brandTeal,
                  }}
                >
                  {money(totalRecovered)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Section note — read-only for members, editable for owner/admin */}
        <NotesSection
          note={data?.note ?? null}
          canEdit={canWrite}
          onSave={(body) => saveNote.mutateAsync(body)}
          onRemove={() => removeNote.mutate()}
          isMutating={saveNote.isPending || removeNote.isPending}
          errorMessage={
            saveNote.error
              ? "Could not save the note. Please try again."
              : removeNote.error
                ? "Could not remove the note. Please try again."
                : null
          }
          theme={theme}
        />

        {/* Body */}
        {isLoading ? (
          <LoadingState theme={theme} />
        ) : (
          <div className="overflow-auto flex-1">
            {/* Per-kind sections — both kinds stacked regardless of origin
              tab, each rendered only when that kind has transactions here. */}
            {transactions.length === 0 && (
              <div
                className="px-6 py-8 text-center"
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textDim }}
              >
                No transactions recorded for this section.
              </div>
            )}
            {leaseholdTxs.length > 0 && (
              <KindSection
                stats={leaseholdStats}
                txs={leaseholdTxs}
                kind="leasehold"
                first
                isMobile={isMobile}
                theme={theme}
              />
            )}
            {mineralTxs.length > 0 && (
              <KindSection
                stats={mineralStats}
                txs={mineralTxs}
                kind="mineral"
                first={leaseholdTxs.length === 0}
                isMobile={isMobile}
                theme={theme}
              />
            )}

            {/* Notes — surfaced from any transaction carrying free-form notes */}
            {noteRows.length > 0 && (
              <>
                <div
                  className="px-6 py-3"
                  style={{
                    borderTop: `1px solid ${theme.borderColor}`,
                    borderBottom: `1px solid ${theme.borderColor}`,
                  }}
                >
                  <h3
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: theme.textPrimary,
                    }}
                  >
                    Transaction Notes
                  </h3>
                </div>
                <div className="px-6 py-3 flex flex-col gap-2">
                  {noteRows.map((tx) => (
                    <div
                      key={tx.id}
                      className="rounded-lg px-3 py-2"
                      style={{
                        backgroundColor: theme.bgSurface2,
                        border: `1px solid ${theme.borderSubtle}`,
                      }}
                    >
                      <div className="flex items-center gap-2" style={{ marginBottom: "3px" }}>
                        <span
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "11px",
                            fontWeight: 600,
                            color: txTypeColor(tx.type, Number(tx.net_acres)),
                          }}
                        >
                          {tx.type}
                        </span>
                        <span
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "10px",
                            color: theme.textMuted,
                          }}
                        >
                          {tx.transaction_date}
                        </span>
                        <span
                          style={{
                            fontFamily: "var(--font-data)",
                            fontSize: "10px",
                            color: theme.textDim,
                          }}
                        >
                          {tx.tx_number}
                        </span>
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "12px",
                          color: theme.textPrimary,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {tx.notes}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Wells — same head treatment as the kind sections, but in the
                neutral wells slate with the Units pickaxe: operational
                fixtures, deliberately quieter than either position kind. */}
            <div
              className="px-6 py-3"
              style={{
                borderTop: `1px solid ${theme.borderColor}`,
                borderBottom: `1px solid ${theme.borderColor}`,
                backgroundColor: `${theme.wellsAccent}14`,
              }}
            >
              <h3
                className="inline-flex items-center gap-2"
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: theme.wellsAccent,
                }}
              >
                <Pickaxe size={14} strokeWidth={1.75} aria-hidden style={{ flexShrink: 0 }} />
                Wells
              </h3>
            </div>
            {isMobile ? (
              wells.length === 0 ? (
                <div
                  className="px-6 py-8 text-center"
                  style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textDim }}
                >
                  No wells associated with this section.
                </div>
              ) : (
                <div className="flex flex-col gap-2.5 px-4 py-3">
                  {wells.map((well, i) => (
                    <DataCard
                      key={well.id ?? `${well.well_name}-${i}`}
                      onClick={() => setActiveWell(well)}
                      title={well.well_name}
                      fields={[
                        { label: "Operator", value: well.operator_display ?? well.operator ?? "—" },
                        { label: "Interests", value: well.interests ?? "—" },
                        { label: "WI", value: well.wi ?? "—", mono: true },
                        { label: "RI", value: well.ri ?? "—", mono: true },
                      ]}
                    />
                  ))}
                </div>
              )
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    style={{
                      // Same wells wash as the section head, one step quieter
                      // (mirrors the kind sections' header-row treatment).
                      backgroundColor: `${theme.wellsAccent}0D`,
                      borderBottom: `1px solid ${theme.borderColor}`,
                    }}
                  >
                    {WELL_HEADERS.map((label) => (
                      <th
                        key={label}
                        className="px-4 py-2.5 text-left"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: theme.textDim,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {wells.length === 0 ? (
                    <tr>
                      <td
                        colSpan={WELL_HEADERS.length}
                        className="px-4 py-8 text-center"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "13px",
                          color: theme.textDim,
                        }}
                      >
                        No wells associated with this section.
                      </td>
                    </tr>
                  ) : (
                    wells.map((well, i) => (
                      <tr
                        key={well.id ?? `${well.well_name}-${i}`}
                        onClick={() => setActiveWell(well)}
                        style={{
                          borderBottom: `1px solid ${theme.borderSubtle}`,
                          cursor: "pointer",
                        }}
                      >
                        <td className="px-4 py-2.5">
                          <span
                            style={{
                              fontFamily: "var(--font-data)",
                              fontSize: "11px",
                              fontWeight: 500,
                              color: theme.brandTeal,
                              textDecoration: "underline",
                              textUnderlineOffset: "2px",
                            }}
                          >
                            {well.well_name}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "11px",
                              color: theme.textMuted,
                            }}
                          >
                            {well.operator_display ?? well.operator ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "11px",
                              color: theme.textPrimary,
                            }}
                          >
                            {well.interests ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            style={{
                              fontFamily: "var(--font-data)",
                              fontSize: "11px",
                              color: theme.textPrimary,
                            }}
                          >
                            {well.wi ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            style={{
                              fontFamily: "var(--font-data)",
                              fontSize: "11px",
                              color: theme.textPrimary,
                            }}
                          >
                            {well.ri ?? "—"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {activeWell && <WellSummaryModal well={activeWell} onClose={() => setActiveWell(null)} />}
    </div>
  )
}

interface KindStat {
  label: string
  value: string
  color: string
}

// One stacked kind section: a kind-colored heading badge, a compact stats
// row, and that kind's transactions.
function KindSection({
  stats,
  txs,
  kind,
  first,
  isMobile,
  theme,
}: {
  stats: KindStat[]
  txs: Transaction[]
  kind: "leasehold" | "mineral"
  /** First section in the scroll body — skips the doubled top border. */
  first: boolean
  isMobile: boolean
  theme: ClientTheme
}) {
  const headers = kind === "leasehold" ? LEASEHOLD_TX_HEADERS : MINERAL_TX_HEADERS
  return (
    <>
      {/* Kind identity: icon + title on the same kind wash as the stats band
          below, so the whole section head reads as one shaded block in the
          kind's color (ink blue = leasehold, copper = mineral). */}
      <div
        className="px-6 py-3"
        style={{
          borderTop: first ? undefined : `1px solid ${theme.borderColor}`,
          borderBottom: `1px solid ${theme.borderColor}`,
          backgroundColor: `${theme.kindAccents[kind]}14`,
        }}
      >
        <h3
          className="inline-flex items-center gap-2"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "13px",
            fontWeight: 600,
            color: theme.kindAccents[kind],
          }}
        >
          <KindIcon kind={kind} size={14} theme={theme} />
          {KIND_LABELS[kind]}
        </h3>
      </div>

      {/* Compact kind stats. Always 3 columns even when the mineral section
        only fills 2 — the empty cell keeps its stats aligned with the
        leasehold section above. The band carries a low-alpha wash of the
        kind accent (instead of flat bgSurface2) so each section's KPI area
        reads as that kind's territory in light and dark alike. */}
      <div
        className="grid grid-cols-3 px-6 py-4"
        style={{
          borderBottom: `1px solid ${theme.borderColor}`,
          backgroundColor: `${theme.kindAccents[kind]}14`,
        }}
      >
        {stats.map((stat) => (
          <div key={stat.label} className="flex flex-col gap-1">
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                color: theme.textDim,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              {stat.label}
            </span>
            <span
              style={{
                fontFamily: "var(--font-data)",
                fontSize: "18px",
                fontWeight: 700,
                color: stat.color,
                letterSpacing: "-0.01em",
              }}
            >
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      {isMobile ? (
        <div className="flex flex-col gap-2.5 px-4 py-3">
          {txs.map((tx) => {
            const na = Number(tx.net_acres)
            const typeColor = txTypeColor(tx.type, na)
            // ORRI is leasehold-only; computed in the branch that uses it.
            const orri = kind === "leasehold" ? Number(tx.orri_reserved) : 0
            return (
              <DataCard
                key={tx.id}
                title={tx.tx_number}
                accessory={
                  <span
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: typeColor,
                    }}
                  >
                    {tx.type}
                  </span>
                }
                fields={[
                  { label: "Date", value: tx.transaction_date },
                  {
                    label: "Net Acres",
                    value: <span style={{ color: typeColor }}>{displayNetAcres(tx.type, na)}</span>,
                    mono: true,
                  },
                  ...(kind === "leasehold"
                    ? [
                        { label: "NRI", value: formatNumber(tx.nri, 4), mono: true },
                        {
                          label: "ORRI Res.",
                          value: orri > 0 ? decimalToPercent(orri, 2) : "—",
                          mono: true,
                        },
                      ]
                    : [
                        {
                          label: "Royalty",
                          value: tx.royalty == null ? "—" : formatNumber(tx.royalty, 4),
                          mono: true,
                        },
                        { label: "Legal Desc", full: true, value: tx.legal_desc ?? "—" },
                      ]),
                  { label: "Counterparty", value: tx.counterparty ?? "—" },
                  { label: "Bk/Pg", value: tx.book_page ?? "—" },
                ]}
              />
            )
          })}
        </div>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr
              style={{
                // Same kind wash as the stats band, one step quieter — the
                // header row tags the tx table without competing with it.
                backgroundColor: `${theme.kindAccents[kind]}0D`,
                borderBottom: `1px solid ${theme.borderColor}`,
              }}
            >
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-4 py-2.5 text-left"
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: theme.textDim,
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {txs.map((tx) => {
              const na = Number(tx.net_acres)
              const typeColor = txTypeColor(tx.type, na)
              // ORRI is leasehold-only; computed in the branch that uses it.
              const orri = kind === "leasehold" ? Number(tx.orri_reserved) : 0
              return (
                <tr key={tx.id} style={{ borderBottom: `1px solid ${theme.borderSubtle}` }}>
                  <td className="px-4 py-2.5">
                    <span
                      style={{
                        fontFamily: "var(--font-data)",
                        fontSize: "11px",
                        fontWeight: 500,
                        color: theme.textPrimary,
                      }}
                    >
                      {tx.tx_number}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "11px",
                        color: theme.textMuted,
                      }}
                    >
                      {tx.transaction_date}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: typeColor,
                      }}
                    >
                      {tx.type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span
                      style={{ fontFamily: "var(--font-data)", fontSize: "11px", color: typeColor }}
                    >
                      {displayNetAcres(tx.type, na)}
                    </span>
                  </td>
                  {kind === "leasehold" ? (
                    <>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          style={{
                            fontFamily: "var(--font-data)",
                            fontSize: "11px",
                            color: theme.textPrimary,
                          }}
                        >
                          {formatNumber(tx.nri, 4)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          style={{
                            fontFamily: "var(--font-data)",
                            fontSize: "11px",
                            color: orri > 0 ? theme.textPrimary : theme.textDim,
                          }}
                        >
                          {orri > 0 ? decimalToPercent(orri, 2) : "—"}
                        </span>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          style={{
                            fontFamily: "var(--font-data)",
                            fontSize: "11px",
                            color: tx.royalty == null ? theme.textDim : theme.textPrimary,
                          }}
                        >
                          {tx.royalty == null ? "—" : formatNumber(tx.royalty, 4)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "11px",
                            color: tx.legal_desc ? theme.textPrimary : theme.textDim,
                          }}
                        >
                          {tx.legal_desc ?? "—"}
                        </span>
                      </td>
                    </>
                  )}
                  <td className="px-4 py-2.5">
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "11px",
                        color: theme.textPrimary,
                      }}
                    >
                      {tx.counterparty ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      style={{
                        fontFamily: "var(--font-data)",
                        fontSize: "11px",
                        color: theme.textPrimary,
                      }}
                    >
                      {tx.book_page ?? "—"}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </>
  )
}
